import { Component, OnInit, ViewChild, AfterContentInit, AfterViewInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { map, takeUntil, startWith } from 'rxjs/operators';

import * as moment from 'moment';
import { Moment } from 'moment';

import { CDSService } from 'src/app/shared/services/cds.service';
import * as GLOBAL from 'src/app/shared/globals';
import { DataExchangeService } from 'src/app/shared/services/data-exchange.service';
import { ToolConfig } from 'src/app/shared/types/ToolConfig';
import { DatatableComponent } from '@swimlane/ngx-datatable';

import { Angular5Csv } from 'angular5-csv/dist/Angular5-csv';
import { NgxgRequest } from 'src/app/core/comm/ngxg-request';
import { TabLoadingService } from '../../utils/tab-loading.service';

@Component({
    templateUrl: './disease.component.html',
    styleUrls: ['./disease.component.scss']
})
export class DiseaseComponent extends NgxgRequest implements OnInit {

    public tabLoading: Boolean;
    private toolConfig: ToolConfig;

    public headerDesc = {

        avgTLwd: 'Mean Temperature During Wetness Period (Farhrenheit)',

        lwd: 'Cumulative Leaf Wetness Duration',

        aRisk: 'Anthracnose Infection Index: Calculated disease infection index based on temperature and leaf wetness duration',
        bRisk: 'Botrytis Infection Index: Calculated disease infection index based on temperature and leaf wetness duration'
    }

    @ViewChild(DatatableComponent) diseaseTable: DatatableComponent;
    public diseaseTableRows: any;

    public diseaseChart: {
        options: any;
        instance?: any;
    };

    public riskMap: Object = { 'L': 'Low', 'M': 'Moderate', 'H': 'High' };

    public bsRangeValue = [moment().subtract(30, 'days').toDate(), moment().toDate()];
    public bsMinMaxDate = [moment('20180826', 'YYYYMMDD').toDate(), moment().startOf('day').toDate()];

    // tslint:disable-next-line:max-line-length
    constructor(private tabLoadingService: TabLoadingService, private cdsService: CDSService, private dataExchangeService: DataExchangeService) {
        super();

    }

    ngOnInit() {

        this.tabLoading = true;

        this.dataExchangeService.getConfig()
            .pipe(takeUntil(this.ngxgUnsubscribe))
            .subscribe(config => {
                if (config !== null) {
                    this.tabLoading = true;
                    this.toolConfig = config;
                    this.bsRangeValue = [this.toolConfig.date.from.toDate(), this.toolConfig.date.to.toDate()];

                    this.initCharts();

                    if (this.toolConfig.season !== null) {
                        this.loadDiseaseHistory(this.toolConfig.season.id, this.toolConfig.date.from, this.toolConfig.date.to);
                    }

                }

            });

    }

    // Function duplicated on disease and weather
    public dateChanged(event): void {

        if (event != null && this.toolConfig.season !== null) {

            if (this.toolConfig.date.from.valueOf() !== moment(event[0]).valueOf() ||
                this.toolConfig.date.to.valueOf() !== moment(event[1]).valueOf()) {

                this.tabLoading = true;
                this.tabLoadingService.setLoading(this.tabLoading);

                this.toolConfig.date.from = moment(event[0]);
                this.toolConfig.date.to = moment(event[1]);
                this.dataExchangeService.setConfig(this.toolConfig);

            }

        }

    }

    private loadDiseaseHistory(seasonID: string, from: Moment, to: Moment): void {

        forkJoin(

            // Anthracnose
            this.cdsService.getDiseaseHistory(seasonID, GLOBAL.anthracnoseID, from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD'))
                .pipe(map(result => this.formatData(result))),

            this.cdsService
                .getDiseaseForecast(seasonID, GLOBAL.anthracnoseID,
                    moment().format('YYYY-MM-DD'), moment().add(3, 'days').format('YYYY-MM-DD'))
                .pipe(map(result => this.formatData(result))),

            // Botrytis
            this.cdsService.getDiseaseHistory(seasonID, GLOBAL.botrytisID, from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD'))
                .pipe(map(result => this.formatData(result))),
            this.cdsService
                .getDiseaseForecast(seasonID, GLOBAL.botrytisID,
                    moment().format('YYYY-MM-DD'), moment().add(3, 'days').format('YYYY-MM-DD'))
                .pipe(map(result => this.formatData(result)))

        )
            .pipe(takeUntil(this.ngxgUnsubscribe))
            .subscribe(([anthracnose, anthracnoseForecast, botrytis, botrytisForecast]) => {

                // Build disease table
                let dates = [];

                anthracnose.table.forEach((aTable, index) => {
                    dates.push({date: aTable.date, dtms: aTable.dtms, aIndex: index, bIndex: null});
                });

                botrytis.table.forEach((bTable, index) => {
                    let dtIndex = dates.findIndex(dts => dts.date == bTable.date);
                    if(dtIndex == -1) {
                        dates.push({date: bTable.date, dtms: bTable.dtms, aIndex: null, bIndex: index});
                    } else {
                    dates[dtIndex].bIndex = index;
                    }
                });

                dates = dates.sort((a, b) => {return b.dtms - a.dtms;});

                const rows = [];
                dates.forEach(dts => {

                    let row = {
                        date: dts.date,
                        lwd: null,
                        avgTLwd: null,
                        aRisk: 'NA',
                        bRisk: 'NA'
                    };

                    if(anthracnose.table[dts.aIndex] != null){
                        row.lwd = (anthracnose.table[dts.aIndex].lwd).toFixed(2);
                        row.avgTLwd = (anthracnose.table[dts.aIndex].lwd == 0) ? '-' : Math.round(anthracnose.table[dts.aIndex].avgTLwd * 1.8 + 32);
                        row.aRisk = (anthracnose.table[dts.aIndex].iIndex).toFixed(2) + ' <small>(' + this.riskMap[anthracnose.table[dts.aIndex].risk] + ')</small>';
                    }

                    if(botrytis.table[dts.bIndex] != null) {
                        if(anthracnose.table[dts.aIndex] == null) {
                            row.lwd = (botrytis.table[dts.bIndex].lwd).toFixed(2);
                            row.avgTLwd = (botrytis.table[dts.bIndex].lwd == 0) ? '-' : Math.round(botrytis.table[dts.bIndex].avgTLwd * 1.8 + 32);
                        }
                        row.bRisk = (botrytis.table[dts.bIndex].iIndex).toFixed(2) + ' <small>(' + this.riskMap[botrytis.table[dts.bIndex].risk] + ')</small>';
                    }

                    rows.push(row);
                });

                // // Build disease table
                // const row = [];
                // anthracnose.table.map((data, index) => {
                //     row.push({
                //         date: data.date,
                //         lwd: (data.lwd).toFixed(2),
                //         avgT: Math.round(data.avgT * 1.8 + 32),
                //         aRisk: (data.iIndex).toFixed(2) + ' <small>(' + this.riskMap[data.risk] + ')</small>',
                //         bRisk: (botrytis.table[index].iIndex).toFixed(2) + ' <small>(' +
                //             this.riskMap[botrytis.table[index].risk] + ')</small>'
                //     });
                // });
                this.diseaseTableRows = rows;

                // Build disease chart
                // timeout to wait for the chart to load
                setTimeout(() => {

                    if (this.diseaseChart.instance != null) {
                        this.diseaseChart.instance.series[0].setData(anthracnose.chart);
                        this.diseaseChart.instance.series[2].setData(botrytis.chart);

                        // Disease chart forecast
                        if (to.startOf('day').isSame(moment().startOf('day'))) {

                            // Fix forecast line when time < noon
                            if (anthracnoseForecast.chart[0][0] === moment().startOf('day').valueOf()) {
                                anthracnoseForecast.chart.splice(0, 1);
                                botrytisForecast.chart.splice(0, 1);
                            }

                            this.diseaseChart.instance.series[1].setData(
                                [anthracnose.chart[(anthracnose.chart).length - 1]].concat(anthracnoseForecast.chart)
                            );

                            this.diseaseChart.instance.series[3].setData(
                                [botrytis.chart[(botrytis.chart).length - 1]].concat(botrytisForecast.chart)
                            );

                        } else {
                            this.diseaseChart.instance.series[1].setData(null);
                            this.diseaseChart.instance.series[3].setData(null);
                        }

                        this.diseaseChart.instance.zoom();
                        this.diseaseChart.instance.hideLoading();
                    }
                });

                this.tabLoading = false;
                this.tabLoadingService.setLoading(false);

            });
    }

    public getRiskColor({ value }): any {

        // Regex must be used instead
        const risk = value.substring(12, value.length - 8);

        if (risk === '(High)') {
            return ' highRiskColor';
        } else if (risk === '(Moderate)') {
            return ' moderateRiskColor';
        } else {
            return ' lowRiskColor';
        }

    }

    public saveChartInstance(instance: any): void {
        this.diseaseChart.instance = instance;
        instance.showLoading('Loading disease data.');
    }

    private formatData(result): any {

        if (result !== null) {
            return {
                chart: result.data.diseaseSimulation.map(res => {
                    return [
                        moment(res.inf.dtgr, 'YYYY-MM-DD').valueOf(),
                        res.iIndex
                    ];
                }),
                table: result.data.diseaseSimulation.map(res => {
                    return {
                        date: res.inf.dtgr,
                        dtms: moment(res.inf.dtgr, 'YYYY-MM-DD').valueOf(),
                        lwd: res.data.lwd.wetHAcc,
                        avgTLwd: res.data.avgTLwd,
                        iIndex: res.iIndex,
                        risk: res.risk
                    };

                })
            };
        } else {
            return result;
        }

    }

    public initCharts(): void {

        this.diseaseChart = {
            options: {

                chart: {
                    height: '600px',
                    marginRight: 0,
                    marginLeft: 60,
                    spacingBottom: 20
                },

                navigator: {
                    enabled: false
                },

                scrollbar: {
                    enabled: false
                },

                exporting: {
                    buttons: {
                        contextButton: {
                            enabled: false
                        }
                    },
                    sourceWidth: 1200
                },

                rangeSelector: {
                    enabled: false,
                    selected: 0,
                    inputEnabled: false,
                    allButtonsEnabled: true,
                    buttons: [{
                        id: 0,
                        type: 'all',
                        text: 'ALL'
                    }, {
                        id: 1,
                        type: 'day',
                        count: 29,
                        text: '30 days'
                    }, {
                        id: 2,
                        type: 'day',
                        count: 6,
                        text: '7 days',
                    }],
                    buttonTheme: {
                        width: 60
                    },
                    labelStyle: {
                    }
                },

                tooltip: {
                    valueDecimals: 2,
                    xDateFormat: '<b>%B %d, %Y</b>',
                    split: false,
                    shared: true,
                    style: {
                        fontSize: '12px'
                    }
                },
                yAxis: [{
                    title: {
                        text: 'Infection index'
                    },
                    labels: {
                        y: 5
                    },
                    opposite: false,
                    min: 0,
                    max: 1,
                    gridLineWidth: 0,
                    minorGridLineWidth: 0,
                    alternateGridColor: null,
                    // tickInterval: 0.2,
                    showLastLabel: true,
                    tickPositions: [0, .15, .50, 1],
                    plotBands: [{
                        color: 'rgba(215, 255, 179, 0.57)',
                        from: .9,
                        to: 1,
                        label: {
                            text: 'Anthracnose',
                            align: 'left',
                            style: {
                                fontSize: '18px',
                                fontWeight: 'bold'
                            },
                            y: -25
                        }
                    }, {// No Risk
                        from: 0,
                        to: 0.15,
                        color: 'rgba(33, 238, 11, 0.8)',
                        label: {
                            text: 'Low risk',
                            style: {
                                color: '#000'
                            }
                        }
                    }, {// Moderate risk
                        from: 0.15,
                        to: 0.5,
                        color: 'rgba(255, 255, 0, 0.8)',
                        label: {
                            text: 'Moderate risk',
                            style: {
                                color: '#000'
                            }
                        }
                    }, {// High Risk
                        from: 0.5,
                        to: 1,
                        color: 'rgba(255, 0, 0, 0.8)',
                        label: {
                            text: 'High risk',
                            style: {
                                color: '#000'
                            }
                        }
                    }
                    ],

                    height: '42%',
                    top: '4%',
                }, {
                    title: {
                        text: 'Infection index'
                    },
                    labels: {
                        y: 5
                    },
                    opposite: false,
                    min: 0,
                    max: 1,
                    gridLineWidth: 0,
                    minorGridLineWidth: 0,
                    alternateGridColor: null,
                    showLastLabel: true,
                    tickPositions: [0, .50, .70, 1],
                    plotBands: [{
                        color: 'rgba(215, 255, 179, 0.57)',
                        from: .9,
                        to: 1,
                        label: {
                            text: 'Botrytis',
                            align: 'left',
                            style: {
                                fontSize: '18px',
                                fontWeight: 'bold'
                            },
                            y: -25
                        }
                    }, {// No Risk
                        from: 0,
                        to: 0.5,
                        color: 'rgba(33, 238, 11, 0.8)',
                        label: {
                            text: 'Low risk',
                            style: {
                                color: '#000'
                            }
                        }
                    }, {// Moderate risk
                        from: 0.5,
                        to: 0.7,
                        color: 'rgba(255, 255, 0, 0.8)',
                        label: {
                            text: 'Moderate risk',
                            style: {
                                color: '#000'
                            }
                        }
                    }, {// High Risk
                        from: 0.7,
                        to: 1,
                        color: 'rgba(255, 0, 0, 0.8)',
                        label: {
                            text: 'High risk',
                            style: {
                                color: '#000'
                            }
                        }
                    }
                    ],
                    height: '42%',
                    top: '57%',
                    offset: 0,
                    lineWidth: 0
                }],

                xAxis: {
                    labels: {
                        overflow: 'justify'
                    },
                    type: 'datetime',
                    plotLines: [{
                        color: 'rgba(0, 95, 158, 0.9)',
                        dashStyle: 'dash',
                        value: moment().startOf('day').valueOf(),
                        width: 2,
                        zIndex: 5,
                        label: {
                            text: 'Forecast',
                            y: 10,
                            style: {
                                color: '#FFF',
                                fontWeight: 'bold'
                            }
                        }
                    }],
                    lineWidth: 0,
                    tickWidth: 0
                },
                series: [{
                    name: 'Anthracnose',
                    type: 'spline',
                    data: [],
                    color: '#212121',
                    showInLegend: false,
                    yAxis: 0
                }, {
                    name: 'Anthracnose Forecast',
                    type: 'spline',
                    dashStyle: 'shortDash',
                    data: [],
                    color: '#212121',
                    showInLegend: false,
                    yAxis: 0
                }, {
                    name: 'Botrytis',
                    type: 'spline',
                    data: [],
                    color: '#212121',
                    showInLegend: false,
                    yAxis: 1
                }, {
                    name: 'Botrytis Forecast',
                    type: 'spline',
                    dashStyle: 'shortDash',
                    data: [],
                    color: '#212121',
                    showInLegend: false,
                    yAxis: 1
                }],
                credits: {
                    enabled: false
                }
            }
        };

    }

    // filename duplicated -- fix
    public exportCharts(event: any, type: string): void {

        const filename = this.toolConfig.season.weatherStation.name.toUpperCase() + '_' +
            this.toolConfig.date.from.format('YYYYMMDD') + '_' + this.toolConfig.date.to.format('YYYYMMDD');

        this.diseaseChart.instance.exportChart({
            type: type,
            filename: filename
        }, {
                chart: {
                    // Fix issue that causes chart to not export more than once
                    events: { load: function () { } },

                    marginTop: 30,
                    marginBottom: 80
                }
            });
    }

    // filename duplicated -- fix
    public tableToCSV(event): any {

        const reRisk = /^([.0-9]+)\s.*\(([A-Za-z]+)\).*/;
        const tableToExport = [];

        this.diseaseTableRows.forEach(diseaseRow => {

            let aRiskRes = (diseaseRow.aRisk == 'NA')? 'NA': diseaseRow.aRisk.match(reRisk)[1];
            let bRiskRes = (diseaseRow.bRisk == 'NA')? 'NA': diseaseRow.bRisk.match(reRisk)[1];

            tableToExport.push({
                station: this.toolConfig.season.weatherStation.name.toUpperCase(),
                date: diseaseRow.date,

                LWD: diseaseRow.lwd,
                temp_F_avg_during_LWD: diseaseRow.avgTLwd,

                AnthracnoseII: aRiskRes,
                BotrytisII: bRiskRes,
            });

        });

        const filename = this.toolConfig.season.weatherStation.name.toUpperCase() + '_' +
            this.toolConfig.date.from.format('YYYYMMDD') + '_' + this.toolConfig.date.to.format('YYYYMMDD');

        const options = {
            fieldSeparator: ',',
            quoteStrings: '"',
            decimalseparator: '.',
            showLabels: true,
            headers: Object.keys(tableToExport[0]),
            showTitle: false,
            title: filename,
            useBom: true,
        };

        return new Angular5Csv(tableToExport, filename, options);

    }

}
